import { CriterionFilterFields } from '@/components/admin/criteres/criterion-filter-fields';
import { FilterBar } from '@/components/admin/versioned/filter-bar';
import { FilterPanel } from '@/components/admin/versioned/filter-panel';
import { FilterSummary } from '@/components/admin/versioned/filter-summary';
import { VersionedGrid } from '@/components/admin/versioned/versioned-grid';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { duplicateCriterion, fetchCriteria, restoreCriterion } from '@/lib/api';
import {
  activeCriterionFilters,
  criterionAuthors,
  criterionCounts,
  criterionFiltersOf,
  criterionSearchOf,
  filterCriteria,
  hasActiveCriterionFilters,
  hasHiddenCriterionFilter,
  readCriterionFilters,
  resetCriterionFilters,
} from '@/lib/criterion-filters';
import { SORT_LABEL, criterionFacets } from '@/lib/criterion-labels';
import { CRITERION_WORDING } from '@/lib/criterion-wording';
import { useVersionedDuplication } from '@/lib/versioned-duplication';
import { versionedRefusal, versionedRefusalOf } from '@/lib/versioned-labels';
import type { CriterionCard, CriterionFilters } from '@matter_extractor/shared';
import { criterionSortSchema } from '@matter_extractor/shared';
import { Alert, Spinner } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * La bibliothèque de critères - the screen that replaces the couture of #24
 * (refonte modulaire §4, §6; issues #38 and #39).
 *
 * Four components and eight functions of the versioned layer, plus the three
 * things a criterion contributes: its wording, its own filter control, and the
 * facet its cards carry (« 3 exemples »). The layer holds the filtering, the
 * sparse URL, the chips, the counting and the grid, so a persona library is the
 * same file with three other values.
 *
 * ## The whole library in one read, filtered in the page
 *
 * `GET /api/criteria` serves every criterion, retired ones included, and the
 * filters of §4 are applied by `lib/versioned-filters.ts`. That decision
 * belongs to `criterionListSchema` and is argued there; what it buys this
 * screen is that a keystroke in « Nom » costs a `filter()` rather than a round
 * trip, and that the rule set is asserted without a base.
 *
 * ## The filters live in the URL
 *
 * The régime of `/admin/journal?session=` and `/admin/entretiens?reference=`:
 * a filtered list is exactly what somebody pastes into a team thread, and a
 * reload must not throw away what an operator typed. `validateSearch` NEVER
 * throws - a typo in a pasted link degrades to the neutral filter rather than
 * to a blank screen.
 *
 * ## One cache key, one shape (issue #16)
 *
 * `['criteria', email]` holds a `CriterionList` and nothing else. The editor
 * reads `['criterion', email, id]`, which is a different shape under a
 * different prefix - the trap of #16 is two components agreeing on a key and
 * disagreeing on what is under it, and `useQuery` is generic per component so
 * nothing would catch it.
 */

export const Route = createFileRoute('/admin/criteres/')({
  validateSearch: readCriterionFilters,
  component: CriteresPage,
});

const INTRO =
  'Ce que le scribe doit reconnaître dans ce que la personne dit : un nom, une direction d’IA, des exemples positifs. La bibliothèque est une référence vivante : modifier un critère change les sessions à venir, jamais celles qui ont eu lieu.';

function CriteresPage() {
  const { email } = useAdminSession();
  /*
    The URL carries only what is NOT neutral (see `VersionedSearch`), and the
    pure rules take the total state. `versionedFiltersOf` is the one place the
    defaults are filled in, so « absent » and « reset » cannot mean two
    different things in two components.
  */
  const filters = criterionFiltersOf(Route.useSearch());
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * Is the « Plus de filtres » panel open?
   *
   * Component state and NOT a search param, deliberately: it is a posture of
   * the reader and not a property of the list. A pasted link carries the
   * filters that shaped what somebody saw; whether the drawer they set them in
   * was open is not part of that. It opens on its own when a link arrives with
   * one of the hidden filters armed, which is the one case where the panel is
   * information rather than furniture.
   */
  const [expanded, setExpanded] = useState(() => hasHiddenCriterionFilter(filters));

  const criteria = useQuery({
    queryKey: ['criteria', email],
    queryFn: fetchCriteria,
    retry: false,
  });

  const rejected = useRejectOn401(criteria.error);

  const restore = useMutation({
    mutationFn: (card: CriterionCard) => restoreCriterion(card.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['criteria', email] }),
  });

  /**
   * « Dupliquer », from the card's « … » menu (§4.2, issue #116).
   *
   * The mutation itself is the layer's (`lib/versioned-duplication.ts`): the
   * gesture is a route of the versioning FACTORY, and the four libraries call
   * it with the three things that differ - the API call, the list's cache key,
   * and the address of the copy's editor, which only this route can type
   * against the route tree.
   */
  const duplicate = useVersionedDuplication<CriterionCard>({
    duplicate: duplicateCriterion,
    listKey: 'criteria',
    onCopy: (criterionId) =>
      void navigate({
        to: '/admin/criteres/$criterionId',
        params: { criterionId },
        search: { copie: true },
      }),
  });

  /**
   * One dimension at a time, merged into the address bar - and REPLACED there,
   * never pushed.
   *
   * The « Nom » field filters as it is typed, so a push would leave one history
   * entry per keystroke: « émotion » costs seven presses of Back before the
   * browser leaves this screen at all. The URL here is derived state - it exists
   * so a filtered list can be pasted into a thread - and derived state does not
   * belong in the back stack. `/admin/entretiens` pushes because it navigates
   * once, on submit.
   */
  const change = (patch: Partial<CriterionFilters>) =>
    navigate({
      to: '/admin/criteres',
      search: criterionSearchOf({ ...filters, ...patch }),
      replace: true,
    });

  const cards = criteria.data?.criteria ?? [];
  const counts = criterionCounts(cards, filters);
  const chips = activeCriterionFilters(filters);
  const refused = versionedRefusalOf(restore.error);
  const duplicateRefused = versionedRefusalOf(duplicate.error);

  return (
    <section className="space-y-5 pb-16">
      <header className="space-y-1">
        {/*
          `/^critères$/i` is what `admin-shell.spec.ts` and the door catalogue
          read: the couture carried this exact heading, and the real screen
          keeps it so those assertions never had a screen to chase.
        */}
        <h1 className="font-bold text-bleu-nuit text-h2">Critères</h1>
        <p className="max-w-[95ch] text-gray-600 text-small">{INTRO}</p>
      </header>

      <FilterBar
        wording={CRITERION_WORDING}
        filters={filters}
        authors={criterionAuthors(cards, filters)}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((was) => !was)}
        onChange={change}
      />

      {expanded ? (
        <FilterPanel
          wording={CRITERION_WORDING}
          filters={filters}
          ownFilters={<CriterionFilterFields filters={filters} onChange={change} />}
          retiredCount={counts.retired}
          onChange={change}
          onReset={() =>
            navigate({
              to: '/admin/criteres',
              search: criterionSearchOf(resetCriterionFilters(filters)),
              replace: true,
            })
          }
          resetDisabled={!hasActiveCriterionFilters(filters)}
        />
      ) : null}

      <FilterSummary
        wording={CRITERION_WORDING}
        chips={chips}
        counts={counts}
        sort={filters.sort}
        sortOptions={criterionSortSchema.options}
        sortLabel={SORT_LABEL}
        onChange={change}
      />

      {duplicate.isError ? (
        <div data-testid="criteres-duplication-refusee">
          <Alert tone="danger">
            {duplicateRefused === null
              ? 'La copie n’a pas pu être écrite. Réessayez dans un instant.'
              : versionedRefusal(duplicateRefused, CRITERION_WORDING.refusal)}
          </Alert>
        </div>
      ) : null}

      {restore.isError ? (
        <div data-testid="criteres-restauration-refusee">
          <Alert tone="danger">
            {refused === null
              ? 'La restauration a échoué. Réessayez dans un instant.'
              : versionedRefusal(refused, CRITERION_WORDING.refusal)}
          </Alert>
        </div>
      ) : null}

      {criteria.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner label="Chargement des critères" />
        </div>
      ) : null}

      {criteria.isError && !rejected ? (
        <p className="text-gray-700">La bibliothèque de critères n’a pas pu être chargée.</p>
      ) : null}

      {criteria.data ? (
        <VersionedGrid
          wording={CRITERION_WORDING}
          cards={filterCriteria(cards, filters)}
          // Why it is empty, worded HERE: only this screen knows whether a
          // filter is armed, and « aucun critère » under a live filter would
          // send an operator to write one they already have.
          emptyMessage={
            chips.length === 0
              ? 'Aucun critère pour l’instant. La carte en pointillé ouvre l’éditeur vide.'
              : 'Aucun critère ne répond à ces filtres. Retirez une pastille, ou réinitialisez.'
          }
          facetsOf={criterionFacets}
          renderCardLink={(card, props) => (
            <Link
              to="/admin/criteres/$criterionId"
              params={{ criterionId: card.id }}
              className={props.className}
            >
              {props.children}
            </Link>
          )}
          renderCreateLink={(props) => (
            <Link
              to="/admin/criteres/nouveau"
              data-testid={props['data-testid']}
              className={props.className}
            >
              {props.children}
            </Link>
          )}
          onRestore={(card) => restore.mutate(card)}
          restoringId={restore.isPending ? (restore.variables?.id ?? null) : null}
          onDuplicate={(card, tag) => duplicate.mutate({ card, tag })}
          duplicatingId={duplicate.isPending ? (duplicate.variables?.card.id ?? null) : null}
        />
      ) : null}
    </section>
  );
}
